import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {PipeId, NodeSpec} from '../execution/graph';
import {PTransformer, PipeContext, Source} from '../execution/pipes';
import {
  DataValue,
  MaterialValue,
  PlateValue,
  PlatedMaterialValue,
  StringValue,
} from '../types/data';
import {WATER as MATERIAL_WATER} from '../types/materials';

export const NODE = {
  id: 'node_plated_material',
  label: 'Material',
  inputs: [
    {
      id: 'material',
      label: 'Material',
      type: {
        kind: 'material',
        contents: undefined,
        tags: undefined,
      },
    },
    {
      id: 'plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'material',
      label: 'Material',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const material = overlay.singleValuePadSource(
      checkExists(node.inputs.get('material')),
      'material',
      () => MATERIAL_WATER,
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );

    const output = new PlateMaterialTransformer(
      overlay.nextIdFor(node),
      /* label= */ undefined,
      material,
      plate,
    );
    overlay.setPadSource(checkExists(node.outputs.get('material')), output);
  },
} as const satisfies NodeSpec;

export class PlateMaterialTransformer extends PTransformer<
  DataValue,
  PlatedMaterialValue
> {
  constructor(
    id: PipeId,
    private readonly label: Source<StringValue> | undefined,
    private readonly material: Source<MaterialValue>,
    private readonly plate: Source<PlateValue>,
  ) {
    super(id, [...(label ? [label] : []), material, plate]);
  }

  async collate(context: PipeContext): Promise<void> {
    const material = checkExists(this.values.get(this.material));
    const materialValue = checkExists(material.value) as MaterialValue;
    const plate = checkExists(this.values.get(this.plate));
    const plateId = (checkExists(plate.value) as PlateValue).id;
    const label = this.label
      ? checkExists(this.values.get(this.label))
      : undefined;
    const labelValue = label
      ? (checkExists(label.value) as StringValue).value
      : materialValue.label;

    const well = context.lab.plate(plateId).getSharedWell(materialValue.id);
    const value: PlatedMaterialValue = {
      kind: 'plated_material',
      material: {
        ...materialValue,
        label: labelValue,
      },
      plate: plateId,
      well: well.id,
    };

    let constraints;
    try {
      constraints = await this.gatherConstraints(value, context);
    } catch (e: unknown) {
      // If a downstream node fails it's not our fault (hopefully)
      await label?.resolve({});
      await material.resolve({});
      await plate.resolve({});
      return;
    }

    await label?.resolve({});
    await material.resolve({});
    await plate.resolve({
      plate: {
        wells: new Map([[well.id, constraints]]),
      },
      value: {
        expected: {
          kind: 'plate',
          id: plateId,
          wells: new Map([[well.id, value]]),
        },
        reason: 'Plate has content',
      },
    });
  }
}
